import { LoadingState } from '../ui';
// src/pages/FriendLanding.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import notify from '../lib/notify';
import { Button as FcButton, Card as FcCard } from '../ui';

export default function FriendLanding() {
  const { friendId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [friendData, setFriendData] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [alreadyFriends, setAlreadyFriends] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(false);

  useEffect(() => {
    // Get current user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id || null);
    });
  }, []);

  useEffect(() => {
    if (friendId) {
      fetchFriendData();
    }
  }, [friendId, currentUserId]);

  const fetchFriendData = async () => {
    setLoading(true);
    try {
      let userId;
      
      // Check if friendId is a UUID (36 chars with hyphens) or 8-char referral code
      const isUUID = friendId.length === 36 && friendId.includes('-');
      
      if (isUUID) {
        // Direct user ID lookup
        userId = friendId;
      } else {
        // Find user by referral code (Friend ID)
        const { data: userData, error: userError } = await supabase
          .from('user_referral_codes')
          .select('user_id')
          .eq('referral_code', friendId.toUpperCase())
          .single();

        if (userError || !userData) {
          notify('Friend not found', 'error');
          navigate('/login');
          return;
        }

        userId = userData.user_id;
      }

      // Parallelize all queries for speed
      const queries = [
        // Profile data
        supabase
          .from('profiles')
          .select('username')
          .eq('id', userId)
          .single(),
        
        // Referral code
        supabase
          .from('user_referral_codes')
          .select('referral_code')
          .eq('user_id', userId)
          .single(),
        
        // Total competitions count
        supabase
          .from('user_selections')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId),
        
        // Wins count
        supabase
          .from('user_selections')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_winner', true),
        
        // Active competitions count
        supabase
          .from('user_selections')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .in('status', ['active', 'pending'])
      ];

      // Add friendship check if user is logged in
      if (currentUserId) {
        const minId = currentUserId < userId ? currentUserId : userId;
        const maxId = currentUserId < userId ? userId : currentUserId;
        
        queries.push(
          supabase
            .from('friendships')
            .select('id')
            .eq('user1_id', minId)
            .eq('user2_id', maxId)
            .maybeSingle(),
          
          supabase
            .from('friend_requests')
            .select('id')
            .or(`and(sender_id.eq.${currentUserId},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${currentUserId})`)
            .eq('status', 'pending')
            .maybeSingle()
        );
      }

      // Execute all queries in parallel
      const results = await Promise.all(queries);

      const [
        { data: profileData, error: profileError },
        { data: referralData },
        { count: totalCompetitions },
        { count: wins },
        { count: activeCompetitions },
        friendshipResult,
        requestResult
      ] = results;

      if (profileError) throw profileError;

      const displayFriendId = referralData?.referral_code || friendId;

      // Set friendship status if user is logged in
      if (currentUserId) {
        setAlreadyFriends(!!friendshipResult?.data);
        setPendingRequest(!!requestResult?.data);
      }

      setFriendData({
        userId,
        username: profileData.username,
        friendId: displayFriendId,
        totalCompetitions: totalCompetitions || 0,
        wins: wins || 0,
        activeCompetitions: activeCompetitions || 0,
        winRate: totalCompetitions > 0 ? Math.round((wins / totalCompetitions) * 100) : 0
      });
    } catch (error) {
      notify('Failed to load friend profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!currentUserId) {
      navigate('/login');
      return;
    }

    if (currentUserId === friendData.userId) {
      notify('You cannot send a friend request to yourself', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: currentUserId,
          receiver_id: friendData.userId,
          status: 'pending'
        });

      if (error) throw error;

      notify('Friend request sent!', 'success');
      setPendingRequest(true);
    } catch (error) {
      notify('Failed to send friend request', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#284239] mx-auto"></div>
          <LoadingState label="Loading friend profile..." />
        </div>
      </div>
    );
  }

  if (!friendData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#284239] to-[#1f332a] rounded-lg shadow-lg p-8 text-white text-center mb-6">
          <FcCard as="div" padding="none" className="w-24 h-24 bg-surface-card rounded-full mx-auto mb-4 flex items-center justify-center">
            <span className="text-5xl text-ui-green font-bold">
              {friendData.username.charAt(0).toUpperCase()}
            </span>
          </FcCard>
          <h1 className="text-3xl font-bold mb-2">@{friendData.username}</h1>
          <p className="text-white/80 text-sm mb-4">Friend ID: {friendData.friendId}</p>
          
          {currentUserId ? (
            <>
              {alreadyFriends ? (
                <div className="bg-green-500/20 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Already Friends
                </div>
              ) : pendingRequest ? (
                <div className="bg-yellow-500/20 text-white px-6 py-3 rounded-lg inline-block">
                  Friend Request Pending
                </div>
              ) : currentUserId === friendData.userId ? (
                <div className="bg-ui-green/20 text-white px-6 py-3 rounded-lg inline-block">
                  This is you!
                </div>
              ) : (
                <FcButton variant="primary" type="submit"
                  onClick={handleSendFriendRequest}
                  className=""
                >
                  👥 Send Friend Request
                </FcButton>
              )}
            </>
          ) : (
            <FcButton variant="primary" type="submit"
              onClick={() => navigate('/login')}
              className=""
            >
              Sign in to Add Friend
            </FcButton>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-ui-green">{friendData.totalCompetitions}</div>
            <div className="text-sm text-content-secondary mt-1">Competitions</div>
          </FcCard>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-ui-success">{friendData.wins}</div>
            <div className="text-sm text-content-secondary mt-1">Wins</div>
          </FcCard>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-action-primary">{friendData.winRate}%</div>
            <div className="text-sm text-content-secondary mt-1">Win Rate</div>
          </FcCard>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-4 text-center">
            <div className="text-3xl font-bold text-ui-green">{friendData.activeCompetitions}</div>
            <div className="text-sm text-content-secondary mt-1">Active</div>
          </FcCard>
        </div>

        {/* Info Card */}
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
          <h2 className="text-xl font-bold text-brand-text mb-4">About</h2>
          <p className="text-content-secondary">
            This is {friendData.username}'s public Footy Coach profile. Connect as friends to see their competition activity and join squads together!
          </p>
        </FcCard>

        {/* Navigation */}
        {currentUserId && (
          <div className="mt-6 text-center">
            <FcButton variant="tertiary" type="submit"
              onClick={() => navigate('/squad')}
              className="hover:underline"
            >
              ← Back to Squad
            </FcButton>
          </div>
        )}
      </div>
    </div>
  );
}
